import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { V4_ICON_BUTTON, V4_ICON_BUTTON_SMALL } from '../../theme/v4Compat';
import {
  Title,
  Loader,
  FarmSelectModal,
  FarmSelectModalHandle,
  VeterinarySelectModal,
  VeterinarySelectModalHandle
} from '../../elements';
import { LoaderSize } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { notifications } from '../../utils';
import { useConfirmation } from '../../hooks/ConfirmationService';
import { BusinessUnitType } from '../../models/farm';
import useAuthContext from '../../hooks/useAuthContext';
import UserRole from '../../constants/user-role';
import { useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import { ApiService } from '../../hooks';
import {
  HealthSystemDomainUsersResultBusinessUnitResponse,
  HealthSystemDomainUsersResultManagementUserViewModel
} from '../../api/HealthSystemApi';

const UserManagementView: React.FC<any> = () => {
  const { state } = useAuthContext();
  const styles = sxStyles(useTheme());
  const apiService = new ApiService();

  const { t } = useTranslation();
  const { id } = useParams();
  const confirmDialog = useConfirmation();

  const [user, setUser] = React.useState<HealthSystemDomainUsersResultManagementUserViewModel | null>(null);

  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(true);

  const farmSelectModalRef = React.useRef<FarmSelectModalHandle>(null!);
  const veterinarySelectModalRef = React.useRef<VeterinarySelectModalHandle>(null!);

  const loadUser = async () => {
    setIsBusy(true);
    setLoaded(false);

    if (!id) {
      notifications.error('User ID is undefined.');
      return;
    }

    await apiService.api.admin
      .adminGetUserBusinessUnitAccessById({ id })
      .then((result) => {
        setUser(result.data);
        setLoaded(true);
      })
      .catch((e) => {
        setLoaded(false);
        notifications.error(e?.error?.message ?? e.toString());
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    loadUser();
  }, [id]);

  React.useEffect(() => {
    if (!loaded) {
      loadUser();
    }
  }, [loaded]);

  const handleLinkFarmToUserClick = () => {
    farmSelectModalRef.current.openModal();
  };

  const handleLinkVeterinaryToUserClick = () => {
    veterinarySelectModalRef.current.openModal();
  };

  const handleLinkBusinessUnitToUser = (id: string | null, role: UserRole) => {
    if (!id || !user) {
      return;
    }

    apiService.api.admin
      .adminLinkBusinessUnitToUser({ businessUnitId: id, userId: user.id, role: role })
      .then(() => {
        setLoaded(false);
      })
      .catch((e) => {
        notifications.error(e?.error?.message ?? e.toString());
      });
  };

  const handleUnlinkBusinessUnitFromUser = (unit: HealthSystemDomainUsersResultBusinessUnitResponse) => {
    if (!user) {
      return;
    }

    confirmDialog({
      dialogId: 'delete-farm-dialog',
      title: t('f-user-management-farm-del-title'),
      message: t('f-user-management-farm-del-msg', { email: user.email }),
      alertSeverity: 'warning'
    }).then(() => {
      apiService.api.admin
        .adminUnlinkBusinessUnitFromUser({ businessUnitId: unit.id, userId: user.id })
        .then(() => {
          setLoaded(false);
        })
        .catch((e) => {
          notifications.error(e?.error?.message ?? e.toString());
        });
    });
  };

  const renderCardAction = (unit: HealthSystemDomainUsersResultBusinessUnitResponse) => {
    return (
      <Grid container spacing={1} direction="column">
        <Grid item>
          <Tooltip arrow title={t('f-com-remove')}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleUnlinkBusinessUnitFromUser(unit)}
              sx={V4_ICON_BUTTON_SMALL}
            >
              <Icon>{'link_off'}</Icon>
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    );
  };

  function getFarms() {
    if (!loaded || !user || !user.businessUnits) {
      return [];
    }

    return user.businessUnits.filter((unit) => unit.type === BusinessUnitType.Farm);
  }

  function getVeterinaries() {
    if (!loaded || !user || !user.businessUnits) {
      return [];
    }

    return user.businessUnits.filter((unit) => unit.type === BusinessUnitType.Veterinary);
  }

  return (
    <Grid container spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex' }}>
          <Icon color="primary" fontSize="large">
            {'contacts'}
          </Icon>
          <Box sx={{ ml: 1, pt: 0.5 }}>
            <Title>{t('f-user-management-view-title')}</Title>
          </Box>
        </Box>
        <Box>
          <IconButton
            color="primary"
            size="medium"
            aria-label={t('f-com-create-new')}
            onClick={() => setLoaded(false)}
            sx={V4_ICON_BUTTON}
          >
            <Icon>refresh</Icon>
          </IconButton>
        </Box>
      </Box>

      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

      {loaded && user && (
        <Paper sx={styles.card} elevation={1}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} lg={4} sx={styles.mb2}>
              <Grid item container spacing={4}>
                <Grid item container xs={4} sx={styles.movingText}>
                  <Typography>{t('f-com-name')}</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography sx={styles.bold}>{user.name}</Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={6} lg={4} sx={styles.mb2}>
              <Grid item container spacing={4}>
                <Grid item container xs={4} sx={styles.movingText}>
                  <Typography>{t('f-com-email')}</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography sx={styles.bold}>{user.email ?? '-'}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Grid item md={6} xs={12} sx={styles.mt2}>
        <Grid container direction="column" spacing={1}>
          <Grid item md={12} id="businessUnitsView">
            <Grid container direction="row" alignItems="center" spacing={1} sx={styles.mh68}>
              <Grid item id="farmsIcon">
                <Icon color="primary" fontSize="large">
                  {'home_work'}
                </Icon>
              </Grid>

              <Grid item>
                <Title>{t('f-farms')}</Title>
              </Grid>

              <Grid item>
                <Chip label={getFarms().length} variant="outlined" />
              </Grid>

              {state.role === UserRole.admin && (
                <Grid item sx={styles.headerRightSection}>
                  <Tooltip arrow title={t('f-user-management-farm-link-tooltip')}>
                    <IconButton color="primary" onClick={handleLinkFarmToUserClick} sx={V4_ICON_BUTTON}>
                      <Icon fontSize="large">{'add_circle'}</Icon>
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
            </Grid>

            {loaded &&
              getFarms().map((unit) => (
                <Paper variant="outlined" key={unit.id}>
                  <List>
                    <div key={unit.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar>{unit.number[0]}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <React.Fragment>
                              <Typography variant="subtitle1" sx={styles.bold}>
                                {unit.name}
                              </Typography>
                              <Typography variant="body1">{unit.role}</Typography>
                            </React.Fragment>
                          }
                        />
                        <ListItemSecondaryAction>{renderCardAction(unit)}</ListItemSecondaryAction>
                      </ListItem>
                    </div>
                  </List>
                </Paper>
              ))}

            {loaded && getFarms().length === 0 && <Alert severity="warning">{t('f-no-data')}</Alert>}
          </Grid>
        </Grid>

        <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
      </Grid>

      <Grid item md={6} xs={12} sx={styles.mt2}>
        <Grid container direction="column" spacing={1}>
          <Grid item md={12} id="vetContactsView">
            <Grid container direction="row" alignItems="center" spacing={1} sx={styles.mh68}>
              <Grid item id="vetIcon">
                <Icon color="primary" fontSize="large">
                  {'local_pharmacy'}
                </Icon>
              </Grid>

              <Grid item>
                <Title>{t('f-veterinaries')}</Title>
              </Grid>

              <Grid item>
                <Chip label={getVeterinaries().length} variant="outlined" />
              </Grid>

              {state.role === UserRole.admin && (
                <Grid item sx={styles.headerRightSection}>
                  <Tooltip arrow title={t('f-user-management-farm-link-tooltip')}>
                    <IconButton color="primary" onClick={handleLinkVeterinaryToUserClick} sx={V4_ICON_BUTTON}>
                      <Icon fontSize="large">{'add_circle'}</Icon>
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
            </Grid>

            {loaded &&
              getVeterinaries().map((unit) => (
                <Paper variant="outlined" key={unit.id}>
                  <List>
                    <div key={unit.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar>{unit.number[0]}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <React.Fragment>
                              <Typography variant="subtitle1" sx={styles.bold}>
                                {unit.name}
                              </Typography>
                              <Typography variant="body1">{unit.address}</Typography>
                            </React.Fragment>
                          }
                        />
                        <ListItemSecondaryAction>{renderCardAction(unit)}</ListItemSecondaryAction>
                      </ListItem>
                    </div>
                  </List>
                </Paper>
              ))}

            {loaded && getVeterinaries().length === 0 && <Alert severity="warning">{t('f-no-data')}</Alert>}
          </Grid>
        </Grid>

        <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
      </Grid>

      <div className="hidden">
        <FarmSelectModal
          ref={farmSelectModalRef}
          farm={null}
          disableAny
          onChange={(farm) => handleLinkBusinessUnitToUser(farm ? farm.id : null, UserRole.admin)}
          excludeIds={[...(user?.businessUnits || []).map((unit) => unit.id)]}
        />
      </div>

      <div className="hidden">
        <VeterinarySelectModal
          ref={veterinarySelectModalRef}
          veterinary={null}
          disableAny
          onChange={(veterinary) =>
            handleLinkBusinessUnitToUser(veterinary ? veterinary.id : null, UserRole.veterinary)
          }
          excludeIds={[...(user?.businessUnits || []).map((unit) => unit.id)]}
        />
      </div>
    </Grid>
  );
};

export default UserManagementView;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column',
      minHeight: 200
    },
    formControl: {
      margin: theme.spacing(1)
    },
    bold: {
      fontWeight: 'bold'
    },
    headerRightSection: {
      flex: 1,
      textAlign: 'right',
      flexDirection: 'row-reverse'
    },
    displayNone: {
      display: 'none'
    },
    badge: {
      right: -3,
      top: 13,
      border: `2px solid ${theme.palette.background.paper}`,
      padding: '0 4px'
    },
    mb3: {
      marginBottom: theme.spacing(3)
    },
    mb2: {
      marginBottom: theme.spacing(2)
    },
    mt3: {
      marginTop: theme.spacing(3)
    },
    mt2: {
      marginTop: theme.spacing(2)
    },
    pl3: {
      paddingLeft: theme.spacing(3)
    },
    movingText: {
      '@media (min-width:960px)': {
        // background: "red",
        justifyContent: 'flex-end',
        textAlign: 'end'
      }
    },
    card: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(3),
      width: '100%'
    },
    mh68: {
      minHeight: 68
    }
  }) as const;
